import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { get } from "../lib/api";
import { useDebounced, useDocumentTitle, useLoad, useStoredState, useQueryState, useOnChange, useRememberListUrl } from "../lib/hooks";
import { formatDay, plural } from "../lib/format";
import { CLOSED_STAGES, LEAD_SOURCES, OPEN_STAGES, compactEUR, daysInStageText, leadName, stageIcon, stageLabel, stallFlag } from "../lib/leads";
import { todayISO } from "../../helpers/datetime";
import { Button, Card, Empty, ErrorBox, Loading, Menu, MenuItem, PageHead, Pager, SearchInput, Segment, Select, Toggle } from "../ui";
import { LeadSourceBadge, LeadValue, NextStepLine, StageBadge, StallFlag } from "../components/LeadParts";
import MoveStageModal from "../components/MoveStageModal";
import { useShell } from "../shell/context";

const PAGE_SIZE = 50;

const LIST_STAGES = [
    { value: "open", label: "Open stages" },
    ...OPEN_STAGES.map((s) => ({ value: s, label: stageLabel(s) })),
    { value: "closed", label: "Won and lost" },
    ...CLOSED_STAGES.map((s) => ({ value: s, label: stageLabel(s) })),
    { value: "all", label: "All stages" },
];

const listParams = (stage) => {
    if (stage === "open") return { status: "open" };
    if (stage === "closed") return { status: "closed" };
    if (stage === "all") return { status: "all" };
    return { stage };
};

// Query-string filters from detail pages: ?company_id=…&contact_id=…&view=list
function useLinkedFilters() {
    const location = useLocation();
    const history = useHistory();
    const params = new URLSearchParams(location.search);
    const clear = (key) => {
        params.delete(key);
        history.replace({ pathname: location.pathname, search: params.toString() });
    };
    return { companyId: params.get("company_id") || "", contactId: params.get("contact_id") || "", view: params.get("view") || "", label: params.get("label") || "", clear };
}

export default function Leads() {
    useDocumentTitle("Leads");
    useRememberListUrl();
    const { version, openLead, openLog } = useShell();
    const linked = useLinkedFilters();
    const [storedView, setStoredView] = useStoredState("hz-leads-view", "board");
    const view = linked.view === "list" ? "list" : storedView;
    const [q, setQ] = useQueryState("q", "");
    const [stalledOnly, setStalledOnly] = useQueryState("stalled", false);
    const [source, setSource] = useQueryState("source", "");
    const [tag, setTag] = useQueryState("tag", "");
    const [stage, setStage] = useQueryState("stage", "open");
    const [page, setPage] = useQueryState("page", 1);
    const [move, setMove] = useState(null); // {lead, to}
    const dq = useDebounced(q.trim(), 250);
    const today = todayISO();

    useOnChange([dq, stalledOnly, source, tag, stage, linked.companyId, linked.contactId], () => setPage(1));

    const tags = useLoad(() => get("v1/crm/tags"), []);
    const common = { source, tag };

    const pipeline = useLoad(() => get("v1/crm/pipeline", { ...common, stalled_only: stalledOnly ? "true" : undefined }), [source, tag, stalledOnly, version]);
    const closed = useLoad(() => {
        if (view !== "board" || stalledOnly) return Promise.resolve(null);
        const one = (s) => get("v1/crm/leads", { ...common, stage: s, q: dq, company_id: linked.companyId, contact_id: linked.contactId, page_size: 20 });
        return Promise.all(CLOSED_STAGES.map(one)).then(([won, lost]) => ({ won, lost }));
    }, [view, stalledOnly, source, tag, dq, linked.companyId, linked.contactId, version]);
    const list = useLoad(() => {
        if (view !== "list") return Promise.resolve(null);
        return get("v1/crm/leads", {
            ...common,
            ...listParams(stage),
            q: dq,
            company_id: linked.companyId,
            contact_id: linked.contactId,
            stalled_only: stalledOnly ? "true" : undefined,
            page,
            page_size: PAGE_SIZE,
        });
    }, [view, stage, source, tag, dq, stalledOnly, page, linked.companyId, linked.contactId, version]);

    // The pipeline endpoint has no q / company / contact filter: apply them here.
    const boardLeads = useMemo(() => {
        const rows = (pipeline.data && pipeline.data.data) || [];
        const ql = dq.toLocaleLowerCase("tr-TR");
        return rows.filter((l) => {
            if (linked.companyId && l.company_id !== linked.companyId) return false;
            if (linked.contactId && l.primary_contact_id !== linked.contactId && l.introduced_by_contact_id !== linked.contactId) return false;
            if (!ql) return true;
            return [l.title, l.company_name, l.primary_contact_name].some((s) => (s || "").toLocaleLowerCase("tr-TR").includes(ql));
        });
    }, [pipeline.data, dq, linked.companyId, linked.contactId]);

    const filteredBoard = !!(dq || linked.companyId || linked.contactId);
    const stages = (pipeline.data && pipeline.data.stages) || [];
    const totals = stages.reduce((a, s) => ({ count: a.count + s.count, value: a.value + (s.value_eur || 0), flagged: a.flagged + s.stalled + s.no_next_step }), { count: 0, value: 0, flagged: 0 });

    const sub = pipeline.data
        ? `${plural(totals.count, "open lead")} · ${compactEUR(totals.value)} in the pipeline${totals.flagged ? ` · ${totals.flagged} need attention` : ""}`
        : " ";

    const setView = (v) => {
        setStoredView(v);
        if (linked.view) linked.clear("view");
    };

    return (
        <>
            <PageHead
                crumb="CRM"
                title="Leads"
                sub={sub}
                actions={
                    <>
                        <Segment options={[{ value: "board", label: "Board" }, { value: "list", label: "List" }]} value={view} onChange={setView} />
                        <Button variant="primary" icon="ri-add-line" onClick={() => openLead()}>New lead</Button>
                    </>
                }
            />
            <Card noBody style={{ marginBottom: view === "board" ? 16 : 0 }}>
                <div className="hz-filterbar" style={view === "board" ? { borderBottom: 0 } : undefined}>
                    <SearchInput value={q} onChange={setQ} placeholder="Filter by title, company or person…" />
                    {view === "list" && <Select value={stage} onChange={(e) => setStage(e.target.value)} options={LIST_STAGES} aria-label="Stage" />}
                    <Select value={source} onChange={(e) => setSource(e.target.value)} placeholder="Any source" options={LEAD_SOURCES} aria-label="Source" />
                    <Select value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Any tag" options={((tags.data && tags.data.data) || []).map((t) => ({ value: t.name, label: t.name }))} aria-label="Tag" />
                    <Toggle on={stalledOnly} onChange={setStalledOnly}>Stalled only</Toggle>
                    {linked.companyId && <FilterChip label={linked.label || "One company"} icon="ri-building-line" onClear={() => linked.clear("company_id")} />}
                    {linked.contactId && <FilterChip label={linked.label || "One person"} icon="ri-user-line" onClear={() => linked.clear("contact_id")} />}
                </div>
            </Card>

            {view === "board" ? (
                <>
                    <ErrorBox error={pipeline.error} onRetry={pipeline.reload} />
                    {pipeline.loading && !pipeline.data ? (
                        <Loading />
                    ) : (
                        <Board
                            stages={stages}
                            leads={boardLeads}
                            filtered={filteredBoard}
                            closed={stalledOnly ? null : closed.data}
                            today={today}
                            onMove={(lead, to) => setMove({ lead, to })}
                            onShowList={(s) => {
                                setStage(s);
                                setView("list");
                            }}
                            onLog={(l) => openLog({ contact: l.primary_contact_id ? { id: l.primary_contact_id, name: l.primary_contact_name } : null, company: { id: l.company_id, name: l.company_name } })}
                        />
                    )}
                </>
            ) : (
                <Card noBody>
                    <ErrorBox error={list.error} onRetry={list.reload} />
                    {list.loading && !list.data ? <Loading /> : <LeadTable rows={(list.data && list.data.data) || []} today={today} />}
                    {list.data && <Pager page={page} maxPage={list.data.max_page} count={list.data.count} onPage={setPage} label={list.data.count === 1 ? "lead" : "leads"} />}
                </Card>
            )}
            {move && <MoveStageModal lead={move.lead} to={move.to} onClose={() => setMove(null)} />}
        </>
    );
}

function FilterChip({ label, icon, onClear }) {
    return (
        <span className="hz-filter-chip">
            <i className={icon}></i>
            {label}
            <button type="button" aria-label="Clear filter" onClick={onClear}>
                <i className="ri-close-line"></i>
            </button>
        </span>
    );
}

// ---------- board ----------

function Board({ stages, leads, filtered, closed, today, onMove, onLog, onShowList }) {
    const [dragging, setDragging] = useState(null);
    const [over, setOver] = useState(null);
    const [expanded, setExpanded] = useStoredState("hz-leads-closed-open", {});

    const dropProps = (stage) => ({
        onDragOver: (e) => {
            if (!dragging) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (over !== stage) setOver(stage);
        },
        onDragLeave: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setOver((o) => (o === stage ? null : o));
        },
        onDrop: (e) => {
            e.preventDefault();
            const lead = dragging;
            setDragging(null);
            setOver(null);
            if (lead && lead.stage !== stage) onMove(lead, stage);
        },
    });
    const colClass = (stage) => (over === stage ? (dragging && dragging.stage === stage ? " drop-same" : " drop-over") : "");

    const card = (l) => (
        <LeadCard
            key={l.id}
            lead={l}
            today={today}
            dragging={dragging && dragging.id === l.id}
            onDragStart={() => setDragging(l)}
            onDragEnd={() => {
                setDragging(null);
                setOver(null);
            }}
            onMove={onMove}
            onLog={onLog}
        />
    );

    return (
        <div className="hz-board">
            {OPEN_STAGES.map((stage) => {
                const total = stages.find((s) => s.stage === stage) || { count: 0, value_eur: 0, stalled: 0, no_next_step: 0 };
                const items = leads.filter((l) => l.stage === stage);
                return (
                    <section key={stage} className={"hz-col" + colClass(stage)} aria-label={stageLabel(stage)} {...dropProps(stage)}>
                        <div className="hz-col-head">
                            <div className="hz-col-title">
                                <i className={stageIcon(stage)}></i>
                                <span className="hz-spacer">{stageLabel(stage)}</span>
                                <span className="hz-col-count" title={filtered ? `${items.length} shown of ${total.count}` : undefined}>
                                    {filtered ? `${items.length}/${total.count}` : total.count}
                                </span>
                            </div>
                            <div className="hz-col-total">
                                <span className="hz-strong" title="Sum of known EUR values">{compactEUR(total.value_eur)}</span>
                                {total.stalled > 0 && <span className="hz-status-danger"><i className="ri-alarm-warning-line"></i> {total.stalled} stalled</span>}
                                {total.no_next_step > 0 && <span className="hz-status-warning"><i className="ri-error-warning-line"></i> {total.no_next_step} no next step</span>}
                            </div>
                        </div>
                        <div className="hz-col-body">
                            {items.length === 0 && <div className="hz-col-empty">{dragging ? `Drop to move to ${stageLabel(stage)}` : "No leads."}</div>}
                            {items.map(card)}
                        </div>
                    </section>
                );
            })}
            {closed &&
                CLOSED_STAGES.map((stage) => {
                    const res = closed[stage] || { count: 0, data: [] };
                    if (!expanded[stage]) {
                        return (
                            <section
                                key={stage}
                                className={"hz-col hz-col-collapsed" + colClass(stage)}
                                title={`Show ${stageLabel(stage).toLowerCase()} leads`}
                                onClick={() => setExpanded((x) => ({ ...x, [stage]: true }))}
                                aria-label={stageLabel(stage)}
                                {...dropProps(stage)}
                            >
                                <i className={stageIcon(stage)} style={{ color: stage === "won" ? "var(--hz-success)" : "var(--hz-slate-700)", fontSize: 16 }}></i>
                                <span className="hz-col-count">{res.count}</span>
                                <span className="hz-col-vlabel">{stageLabel(stage)}</span>
                            </section>
                        );
                    }
                    return (
                        <section key={stage} className={"hz-col" + colClass(stage)} aria-label={stageLabel(stage)} {...dropProps(stage)}>
                            <div className="hz-col-head">
                                <div className="hz-col-title">
                                    <i className={stageIcon(stage)}></i>
                                    <span className="hz-spacer">{stageLabel(stage)}</span>
                                    <span className="hz-col-count">{res.count}</span>
                                    <Button size="sm" variant="ghost" iconOnly icon="ri-subtract-line" title="Collapse" onClick={() => setExpanded((x) => ({ ...x, [stage]: false }))} />
                                </div>
                                <div className="hz-col-total">
                                    {res.count > (res.data || []).length ? `Latest ${(res.data || []).length} of ${res.count} · ` : ""}
                                    <button type="button" className="hz-btn-link" onClick={() => onShowList(stage)}>See all in the list</button>
                                </div>
                            </div>
                            <div className="hz-col-body">
                                {(res.data || []).length === 0 && <div className="hz-col-empty">No {stageLabel(stage).toLowerCase()} leads.</div>}
                                {(res.data || []).map(card)}
                            </div>
                        </section>
                    );
                })}
        </div>
    );
}

function LeadCard({ lead, today, dragging, onDragStart, onDragEnd, onMove, onLog }) {
    const flag = stallFlag(lead, today);
    const cls = ["hz-lead-card", dragging && "dragging", flag && (flag.kind === "stalled" ? "stalled" : "no-next"), !lead.is_open && "closed"].filter(Boolean).join(" ");
    return (
        <article
            className={cls}
            draggable
            onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", lead.id);
                onDragStart();
            }}
            onDragEnd={onDragEnd}
        >
            {flag && (
                <div className={`hz-lead-card-flag ${flag.color}`}>
                    <i className={flag.kind === "stalled" ? "ri-alarm-warning-line" : "ri-error-warning-line"}></i>
                    {flag.label.toUpperCase()}
                    {flag.detail && <span style={{ fontWeight: 400 }}>· {flag.detail}</span>}
                </div>
            )}
            <div className="hz-lead-card-body">
                <div className="hz-lead-card-top">
                    <Link to={`/crm/leads/${lead.id}`} className="hz-lead-name" draggable={false}>{leadName(lead)}</Link>
                    <Menu trigger={(open, toggle) => <Button size="sm" variant="ghost" iconOnly icon="ri-more-2-fill" title="Lead actions" onClick={toggle} />}>
                        {[...OPEN_STAGES, ...CLOSED_STAGES].filter((s) => s !== lead.stage).map((s) => (
                            <MenuItem key={s} onClick={() => onMove(lead, s)}>
                                Move to {stageLabel(s)}
                            </MenuItem>
                        ))}
                        <div className="hz-menu-sep"></div>
                        <MenuItem onClick={() => onLog(lead)}>Log a touchpoint</MenuItem>
                    </Menu>
                </div>
                <div className="hz-lead-sub">
                    {lead.title && (
                        <Link to={`/crm/companies/${lead.company_id}`} draggable={false} style={{ color: "inherit" }}>
                            <i className="ri-building-line"></i> {lead.company_name}
                        </Link>
                    )}
                    {lead.primary_contact_name ? (
                        <Link to={`/crm/contacts/${lead.primary_contact_id}`} draggable={false} style={{ color: "inherit" }}>
                            <i className="ri-user-line"></i> {lead.primary_contact_name}
                        </Link>
                    ) : (
                        !lead.title && <span>No primary contact</span>
                    )}
                </div>
                <LeadValue lead={lead} />
                <NextStepLine lead={lead} today={today} />
                <div className="hz-lead-foot">
                    <LeadSourceBadge source={lead.source} />
                    <span className="hz-spacer"></span>
                    <span>{lead.is_open ? daysInStageText(lead.days_in_stage) : lead.closed_at ? `Closed ${formatDay(lead.closed_at, { short: true, today })}` : ""}</span>
                </div>
            </div>
        </article>
    );
}

// ---------- list ----------

function LeadTable({ rows, today }) {
    const history = useHistory();
    if (rows.length === 0) return <Empty>No leads match these filters.</Empty>;
    return (
        <div className="hz-table-wrap">
            <table className="hz-table">
                <thead>
                    <tr>
                        <th>Lead</th>
                        <th>Stage</th>
                        <th>Primary contact</th>
                        <th>Value</th>
                        <th>Next step</th>
                        <th>In stage</th>
                        <th>Source</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((l) => (
                        <tr key={l.id} style={{ cursor: "pointer" }} onClick={(e) => !e.target.closest("a") && history.push(`/crm/leads/${l.id}`)}>
                            <td>
                                <Link to={`/crm/leads/${l.id}`} className="hz-entity-cell">
                                    <span style={{ minWidth: 0 }}>
                                        <span className="hz-entity-name">{leadName(l)}</span>
                                        <span className="hz-entity-sub">{l.title ? l.company_name : ""}</span>
                                    </span>
                                </Link>
                                {(l.tags || []).length > 0 && (
                                    <div className="hz-row-wrap" style={{ gap: 4, marginTop: 4 }}>
                                        {l.tags.map((t) => <span key={t} className="hz-tag">{t}</span>)}
                                    </div>
                                )}
                            </td>
                            <td><StageBadge stage={l.stage} /></td>
                            <td>{l.primary_contact_name ? <Link to={`/crm/contacts/${l.primary_contact_id}`}>{l.primary_contact_name}</Link> : <span className="hz-muted hz-small">None</span>}</td>
                            <td><LeadValue lead={l} empty={<span className="hz-muted hz-small">Not set</span>} /></td>
                            <td style={{ minWidth: 220 }}>
                                <StallFlag lead={l} today={today} />
                                <div style={{ marginTop: stallFlag(l, today) ? 4 : 0 }}><NextStepLine lead={l} today={today} /></div>
                            </td>
                            <td className="hz-nowrap hz-small">{l.is_open ? daysInStageText(l.days_in_stage) : <span className="hz-muted">Closed {formatDay(l.closed_at, { short: true, today })}</span>}</td>
                            <td><LeadSourceBadge source={l.source} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
